import React, { useCallback, useEffect, useState } from 'react';

import { usePaywallTracking } from '@vimeo/picox';

import TrackingContext from './components/common/TrackingContext';
import CustomModal from './components/custom/CustomModal';
import DefaultModal from './components/default/DefaultModal';
import EnterpriseModal from './components/enterprise/EnterpriseModal';
import { fetchUserData, fetchPlansData } from './utils/api';
import BpTracking from './utils/bpTracking';
import { PLANS } from './utils/constants';
import { modalProps, PlanData, UserData, TemplateType } from './utils/interface';
import { isSeatTierPlan, getDeviceType, hasDisplayPlan } from './utils/utils';

const EXCLUDED_TEMPLATE_TYPES: Array<TemplateType> = ['feature'];

interface State {
  isShowing: boolean;
  userData: null | UserData;
  plansData: null | PlanData[];
  hasTrackedImpression: boolean;
}

function UpsellModal(props: modalProps) {
  const { apiUrl, userConfig } = props;
  const [state, setState] = useState<State>({
    isShowing: false,
    userData: null,
    plansData: null,
    hasTrackedImpression: false,
  });
  const { trackPaywallDismissed } = usePaywallTracking({
    ...props.tracking.paywallTracking,
    isVisible: state.isShowing,
  });

  // Destructure props.onClose to avoid re-rendering the component when it changes
  const propsOnClose = props.onClose;
  const handleClose = useCallback(() => {
    trackPaywallDismissed();
    propsOnClose();
  }, [trackPaywallDismissed, propsOnClose]);

  useEffect(() => {
    const getUserData = async () => {
      const userData = await fetchUserData(apiUrl, userConfig.jwt);

      return userData;
    };

    const getPlansData = async () => {
      return await fetchPlansData(apiUrl, userConfig);
    };

    (async () => {
      const [userData, plansData] = await Promise.all([getUserData(), getPlansData()]);

      setState((prevState: State) => ({
        ...prevState,
        isShowing: true,
        userData: userData,
        plansData: plansData,
      }));
      BpTracking.configure(userData, userConfig.userId);

      if (props.tracking.onOpen) {
        props.tracking.onOpen();
      }
    })();
  }, [props.tracking, apiUrl, userConfig]);

  useEffect(() => {
    const startTime = Date.now();

    if (!state.hasTrackedImpression && state.userData?.membership.type) {
      BpTracking.trackImpression(
        {
          ...props.tracking.params,
          loading_time: (Date.now() - startTime) / 1000,
          is_new_pricing: isSeatTierPlan(state.userData.membership.type),
          device_type: getDeviceType(),
        },
        props.tracking.bpEventVersion,
      );
      setState((prevState: State) => ({
        ...prevState,
        hasTrackedImpression: true,
      }));
    }
  }, [props.tracking, state.userData?.membership.type, state.hasTrackedImpression]);

  const userTier = state.userData?.membership.type;
  let templateType = props.templateType;
  if (
    !EXCLUDED_TEMPLATE_TYPES.includes(props.templateType) &&
    (userTier === PLANS.ADVANCED ||
      userTier === PLANS.PREMIUM ||
      userTier === PLANS.TEAM_LIVE ||
      userTier === PLANS.CUSTOM_SELF_SERVE) &&
    !(hasDisplayPlan(props.modalConfig) && props.modalConfig?.displayPlan)
  ) {
    templateType = 'enterprise';
  }

  let modal;
  switch (templateType) {
    case 'default':
      modal =
        userTier === 'enterprise' ? (
          <EnterpriseModal
            userData={state.userData}
            onClose={handleClose}
            tracking={props.tracking}
            modalConfig={props.modalConfig}
          />
        ) : (
          <DefaultModal
            userData={state.userData}
            plansData={state.plansData}
            modalConfig={{
              hidePlansSummary: true,
              ...props.modalConfig,
            }}
            onClose={handleClose}
            tracking={props.tracking}
          />
        );
      break;
    case 'feature':
      modal = <CustomModal modalConfig={props.modalConfig} onClose={handleClose} />;
      break;
    case 'enterprise':
      modal = (
        <EnterpriseModal
          userData={state.userData}
          onClose={handleClose}
          tracking={props.tracking}
          modalConfig={props.modalConfig}
        />
      );
      break;
    default:
      modal = null;
  }

  if (!state.isShowing) return null;

  return (
    <TrackingContext.Provider value={{ ...props.tracking, device_type: getDeviceType() }}>
      {modal}
    </TrackingContext.Provider>
  );
}

export default UpsellModal;
